import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Mail,
  Phone,
  MapPin,
  Send,
  CheckCircle2,
  Clock,
  MessageSquare
} from 'lucide-react';
import {
  companyInfo,
  budgetOptions,
  serviceOptions
} from '../data/mock';

const Contact = () => {
  const { t } = useTranslation();
  const [selectedService, setSelectedService] = useState('');
  const [selectedBudget, setSelectedBudget] = useState('');

  const contactInfo = [
    {
      icon: Mail,
      title: t('common.email'),
      value: companyInfo.email,
      link: `mailto:${companyInfo.email}`
    },
    {
      icon: Phone,
      title: t('common.phone'),
      value: companyInfo.phone,
      link: `tel:${companyInfo.phone}`
    },
    {
      icon: MapPin,
      title: t('common.address'),
      value: companyInfo.address,
      link: null
    }
  ];

  const benefits = [
    { icon: Clock, text: t('contact.benefit1') },
    { icon: MessageSquare, text: t('contact.benefit2') },
    { icon: CheckCircle2, text: t('contact.benefit3') }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-20 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300 text-sm font-medium mb-6">
            {t('contact.infoBadge')}
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            {t('contact.heroTitle')}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400"> {t('contact.heroHighlight')}</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            {t('contact.heroDesc')}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info Sidebar */}
            <div className="lg:col-span-2 space-y-8">
              {/* Contact Cards */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">{t('contact.infoTitle')}</h2>
                {contactInfo.map((info, index) => (
                  <Card key={index} className="border-0 shadow-md hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      {info.link ? (
                        <a href={info.link} className="flex items-start gap-4 group">
                          <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                            <info.icon className="text-blue-600" size={24} />
                          </div>
                          <div>
                            <p className="text-sm text-slate-500 mb-1">{info.title}</p>
                            <p className="font-medium text-slate-900 group-hover:text-blue-600 transition-colors">
                              {info.value}
                            </p>
                          </div>
                        </a>
                      ) : (
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <info.icon className="text-blue-600" size={24} />
                          </div>
                          <div>
                            <p className="text-sm text-slate-500 mb-1">{info.title}</p>
                            <p className="font-medium text-slate-900">{info.value}</p>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Benefits */}
              <Card className="border-0 bg-slate-50">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">{t('contact.benefitsTitle')}</h3>
                  <ul className="space-y-3">
                    {benefits.map((benefit, index) => (
                      <li key={index} className="flex items-center gap-3 text-slate-600">
                        <benefit.icon className="text-blue-600" size={18} />
                        <span className="text-sm">{benefit.text}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('contact.formTitle')}</h2>
                  <p className="text-slate-600 mb-8">
                    {t('contact.formDesc')}
                  </p>

                  {/* FormSubmit Form */}
                  <form 
                    action="https://formsubmit.co/service@beta-webagentur.de" 
                    method="POST"
                    className="space-y-6"
                  >
                    {/* FormSubmit Configuration (Hidden Fields) */}
                    <input type="hidden" name="_subject" value="Neue Kontaktanfrage von Beta-Webagentur Website" />
                    <input type="hidden" name="_captcha" value="false" />
                    <input type="hidden" name="_template" value="table" />
                    <input type="text" name="_honey" style={{display: 'none'}} />
                    
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('contact.nameLabel')} {t('contact.required')}
                        </label>
                        <Input
                          type="text"
                          name="name"
                          placeholder={t('contact.namePlaceholder')}
                          required
                          className="bg-slate-50 border-slate-200 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('contact.emailLabel')} {t('contact.required')}
                        </label>
                        <Input
                          type="email"
                          name="email"
                          placeholder={t('contact.emailPlaceholder')}
                          required
                          className="bg-slate-50 border-slate-200 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('contact.phoneLabel')}
                        </label>
                        <Input
                          type="tel"
                          name="phone"
                          placeholder={t('contact.phonePlaceholder')}
                          className="bg-slate-50 border-slate-200 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('contact.companyLabel')}
                        </label>
                        <Input
                          type="text"
                          name="company"
                          placeholder={t('contact.companyPlaceholder')}
                          className="bg-slate-50 border-slate-200 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('contact.serviceLabel')}
                        </label>
                        <Select name="service" value={selectedService} onValueChange={setSelectedService}>
                          <SelectTrigger className="bg-slate-50 border-slate-200">
                            <SelectValue placeholder={t('contact.selectPlaceholder')} />
                          </SelectTrigger>
                          <SelectContent>
                            {serviceOptions.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <input type="hidden" name="service" value={selectedService} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('contact.budgetLabel')}
                        </label>
                        <Select name="budget" value={selectedBudget} onValueChange={setSelectedBudget}>
                          <SelectTrigger className="bg-slate-50 border-slate-200">
                            <SelectValue placeholder={t('contact.selectPlaceholder')} />
                          </SelectTrigger>
                          <SelectContent>
                            {budgetOptions.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <input type="hidden" name="budget" value={selectedBudget} />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('contact.messageLabel')} {t('contact.required')}
                      </label>
                      <Textarea
                        name="message"
                        placeholder={t('contact.messagePlaceholder')}
                        required
                        rows={6}
                        className="bg-slate-50 border-slate-200 focus:border-blue-500 resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Send size={18} className="mr-2" />
                      {t('contact.sendButton')}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
